import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import {
  sanitizeBio,
  normalizeUsername,
  isValidUsername,
} from "@/lib/profile";
import ImagesSection from "./components/ImagesSection";
import SecuritySection from "./components/SecuritySection";
import StatusSections from "./components/StatusSections";
import ToggleSwitch from "@/components/base/ToggleSwitch";

export interface EditableFields {
  full_name: string;
  username: string | null;
  bio: string;
  location: string;
  city: string;
  province: string;
  country: string;
  show_location: boolean;
  show_phone: boolean;
  show_business_info: boolean;
  language: string;
}

type SectionId =
  | "info"
  | "foto"
  | "portada"
  | "descripcion"
  | "ubicacion"
  | "privacidad"
  | "idioma"
  | "seguridad"
  | "vendedor"
  | "membresia"
  | "notificaciones";

const SECTIONS: { id: SectionId; label: string; icon: string }[] = [
  { id: "info", label: "Mi información", icon: "ri-user-line" },
  { id: "foto", label: "Mi foto", icon: "ri-camera-line" },
  { id: "portada", label: "Mi portada", icon: "ri-image-line" },
  { id: "descripcion", label: "Mi descripción", icon: "ri-file-text-line" },
  { id: "ubicacion", label: "Mi ubicación", icon: "ri-map-pin-line" },
  { id: "privacidad", label: "Privacidad", icon: "ri-shield-check-line" },
  { id: "idioma", label: "Idioma", icon: "ri-global-line" },
  { id: "seguridad", label: "Seguridad", icon: "ri-lock-line" },
  { id: "vendedor", label: "Configuración de vendedor", icon: "ri-store-2-line" },
  { id: "membresia", label: "Membresía", icon: "ri-verified-badge-line" },
  { id: "notificaciones", label: "Notificaciones", icon: "ri-notification-3-line" },
];

const TEXT_SECTIONS: SectionId[] = [
  "info",
  "descripcion",
  "ubicacion",
  "privacidad",
  "idioma",
];

const inputClass =
  "w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
const labelClass = "mb-1.5 block text-sm font-semibold text-foreground-800";

function friendlyError(message: string): string {
  if (
    message.includes("duplicate key") ||
    message.includes("profiles_username_lower_unique")
  ) {
    return "Ese nombre de usuario ya está en uso. Elegí otro.";
  }
  if (message.includes("profiles_username_reserved")) {
    return "Ese nombre de usuario no está permitido.";
  }
  return message;
}

export default function ProfileConfigPage() {
  const { user, profile, loading, refreshProfile, resetPassword } = useAuth();
  const { showToast } = useToast();

  const [active, setActive] = useState<SectionId>("info");

  const [fullName, setFullName] = useState(profile?.full_name ?? "");
  const [username, setUsername] = useState(profile?.username ?? "");
  const [bio, setBio] = useState(profile?.bio ?? "");
  const [location, setLocation] = useState(profile?.location ?? "");
  const [city, setCity] = useState(profile?.city ?? "");
  const [province, setProvince] = useState(profile?.province ?? "");
  const [country, setCountry] = useState(profile?.country ?? "");
  const [showLocation, setShowLocation] = useState(
    profile?.show_location ?? false
  );
  const [showPhone, setShowPhone] = useState(profile?.show_phone ?? false);
  const [showBusinessInfo, setShowBusinessInfo] = useState(
    profile?.show_business_info ?? false
  );
  const [language, setLanguage] = useState(profile?.language ?? "es");
  const [saving, setSaving] = useState(false);

  if (loading || !profile) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
      </div>
    );
  }

  async function persistFields(fields: EditableFields) {
    if (!user) throw new Error("No estás autenticado.");
    const safeFields = { ...fields, bio: sanitizeBio(fields.bio) };
    const { error } = await supabase
      .from("profiles")
      .update(safeFields)
      .eq("id", user.id);
    if (error) throw new Error(friendlyError(error.message));
    await refreshProfile();
  }

  async function handleSave() {
    const normalized = normalizeUsername(username);
    if (normalized && !isValidUsername(normalized)) {
      showToast(
        "El nombre de usuario debe tener entre 3 y 30 caracteres válidos.",
        "error"
      );
      return;
    }
    setSaving(true);
    try {
      await persistFields({
        full_name: fullName.trim(),
        username: normalized || null,
        bio: bio.trim(),
        location: location.trim(),
        city: city.trim(),
        province: province.trim(),
        country: country.trim(),
        show_location: showLocation,
        show_phone: showPhone,
        show_business_info: showBusinessInfo,
        language,
      });
      showToast("Configuración guardada correctamente.", "success");
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "No se pudo guardar.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleResetPassword() {
    if (!profile.email) return;
    const { error } = await resetPassword(profile.email);
    if (error) {
      showToast(error, "error");
    } else {
      showToast(
        "Te enviamos un enlace para restablecer tu contraseña. Revisá tu correo.",
        "success"
      );
    }
  }

  async function persistAvatar(url: string | null) {
    if (!user) throw new Error("No estás autenticado.");
    const { error } = await supabase
      .from("profiles")
      .update({ avatar_url: url })
      .eq("id", user.id);
    if (error) throw new Error(error.message);
    await refreshProfile();
  }

  async function persistCover(url: string | null) {
    if (!user) throw new Error("No estás autenticado.");
    const { error } = await supabase
      .from("profiles")
      .update({ cover_image: url })
      .eq("id", user.id);
    if (error) throw new Error(error.message);
    await refreshProfile();
  }

  const current = SECTIONS.find((s) => s.id === active) ?? SECTIONS[0];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Barra superior */}
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-4 md:px-6">
        <div className="flex items-center gap-3">
          <Link
            to="/perfil"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100"
          >
            <i className="ri-arrow-left-line"></i>
          </Link>
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-500">
              Configuración
            </h2>
            <h1 className="font-heading text-lg font-bold text-foreground-950">
              {current.label}
            </h1>
          </div>
        </div>
        <Link
          to="/perfil"
          className="hidden cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3.5 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 sm:inline-flex"
        >
          <i className="ri-eye-line"></i>
          Ver mi perfil
        </Link>
      </div>

      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 pb-10 md:flex-row md:px-6">
        {/* Navegación lateral (escritorio) */}
        <aside className="hidden w-60 shrink-0 md:block">
          <nav className="sticky top-6 space-y-1 rounded-lg border border-background-200/70 bg-background-50 p-2">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s.id)}
                className={`flex w-full cursor-pointer items-center gap-3 whitespace-nowrap rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  active === s.id
                    ? "bg-primary-100 text-primary-800"
                    : "text-foreground-600 hover:bg-background-100"
                }`}
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center ${
                    active === s.id ? "text-primary-600" : "text-foreground-400"
                  }`}
                >
                  <i className={s.icon}></i>
                </span>
                {s.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Navegación en píldoras (móvil) */}
        <div className="flex gap-2 overflow-x-auto pb-1 md:hidden">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActive(s.id)}
              className={`inline-flex cursor-pointer shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                active === s.id
                  ? "bg-primary-500 text-white"
                  : "bg-background-100 text-foreground-700"
              }`}
            >
              <i className={s.icon}></i>
              {s.label}
            </button>
          ))}
        </div>

        {/* Contenido */}
        <div className="min-w-0 flex-1">
          {active === "info" && (
            <section className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                Mi información
              </h2>
              <p className="mt-1 text-sm text-foreground-600">
                Estos datos aparecen en tu perfil público.
              </p>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="cfg-full-name" className={labelClass}>
                    Nombre completo
                  </label>
                  <input
                    id="cfg-full-name"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Tu nombre"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cfg-username" className={labelClass}>
                    Nombre de usuario
                  </label>
                  <div className="flex items-center">
                    <span className="flex h-10 items-center rounded-l-md border border-r-0 border-background-200 bg-background-100 px-3 text-sm text-foreground-500">
                      @
                    </span>
                    <input
                      id="cfg-username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(normalizeUsername(e.target.value))}
                      placeholder="tu-usuario"
                      className="w-full rounded-r-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-foreground-400">
                    Único, de 3 a 30 caracteres (letras, números, . _ -).
                  </p>
                </div>
              </div>
            </section>
          )}

          {active === "foto" && (
            <ImagesSection
              title="Mi foto"
              description="Tu foto principal. Formatos JPG, PNG o WEBP (se comprime sola)."
              bucket="avatars"
              aspect="square"
              userId={profile.id}
              currentUrl={profile.avatar_url}
              onPersist={persistAvatar}
            />
          )}

          {active === "portada" && (
            <ImagesSection
              title="Mi portada"
              description="Una imagen grande estilo Facebook para personalizar tu perfil."
              bucket="covers"
              aspect="wide"
              userId={profile.id}
              currentUrl={profile.cover_image}
              onPersist={persistCover}
            />
          )}

          {active === "descripcion" && (
            <section className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                Mi descripción
              </h2>
              <p className="mt-1 text-sm text-foreground-600">
                Cuéntale a los demás quién sos y qué vendés.
              </p>
              <div className="mt-5">
                <label htmlFor="cfg-bio" className={labelClass}>
                  Biografía
                </label>
                <textarea
                  id="cfg-bio"
                  value={bio}
                  maxLength={280}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Ej. Vendedor de tecnología y accesorios electrónicos."
                  rows={4}
                  className={`${inputClass} resize-none`}
                />
                <p className="mt-1.5 text-right text-xs text-foreground-400">
                  {bio.length}/280
                </p>
              </div>
            </section>
          )}

          {active === "ubicacion" && (
            <section className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                Mi ubicación
              </h2>
              <p className="mt-1 text-sm text-foreground-600">
                Solo se mostrará si activás "Mostrar ubicación" en Privacidad.
              </p>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="cfg-location" className={labelClass}>
                    Ubicación (texto público)
                  </label>
                  <input
                    id="cfg-location"
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Ej. Verón-Punta Cana"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cfg-city" className={labelClass}>
                    Ciudad
                  </label>
                  <input
                    id="cfg-city"
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Punta Cana"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cfg-province" className={labelClass}>
                    Provincia
                  </label>
                  <input
                    id="cfg-province"
                    type="text"
                    value={province}
                    onChange={(e) => setProvince(e.target.value)}
                    placeholder="La Altagracia"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cfg-country" className={labelClass}>
                    País
                  </label>
                  <input
                    id="cfg-country"
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="República Dominicana"
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={showLocation}
                      onChange={(e) => setShowLocation(e.target.checked)}
                      className="h-4 w-4 cursor-pointer rounded border-background-300 accent-primary-500"
                    />
                    <span className="text-sm text-foreground-700">
                      Mostrar mi ubicación públicamente
                    </span>
                  </label>
                </div>
              </div>
            </section>
          )}

          {active === "privacidad" && (
            <section className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                Privacidad
              </h2>
              <p className="mt-1 text-sm text-foreground-600">
                Decidí qué información aparece en tu perfil público.
              </p>
              <div className="mt-5 space-y-4">
                {[
                  {
                    checked: showLocation,
                    onChange: setShowLocation,
                    title: "Mostrar ubicación general",
                    desc: "Ciudad y provincia (nunca coordenadas exactas).",
                    icon: "ri-map-pin-line",
                  },
                  {
                    checked: showPhone,
                    onChange: setShowPhone,
                    title: "Mostrar teléfono comercial",
                    desc: "Tu teléfono/WhatsApp comercial será visible.",
                    icon: "ri-phone-line",
                  },
                  {
                    checked: showBusinessInfo,
                    onChange: setShowBusinessInfo,
                    title: "Mostrar información comercial",
                    desc: "Nombre de tienda y datos del negocio.",
                    icon: "ri-store-2-line",
                  },
                ].map((opt) => (
                  <div
                    key={opt.title}
                    className="flex items-start gap-3 rounded-md border border-background-200 bg-background-50 p-4"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                      <i className={opt.icon}></i>
                    </span>
                    <span className="flex-1">
                      <span className="block text-sm font-semibold text-foreground-900">
                        {opt.title}
                      </span>
                      <span className="block text-xs text-foreground-500">
                        {opt.desc}
                      </span>
                    </span>
                    <ToggleSwitch
                      checked={opt.checked}
                      onChange={opt.onChange}
                      label={opt.title}
                    />
                  </div>
                ))}
                <div className="flex items-start gap-3 rounded-md border border-accent-200 bg-accent-100/50 p-4">
                  <i className="ri-shield-check-line text-accent-700"></i>
                  <p className="text-sm text-accent-900">
                    Tus documentos de identidad, correo privado y coordenadas
                    exactas <strong>nunca</strong> se muestran públicamente.
                  </p>
                </div>
              </div>
            </section>
          )}

          {active === "idioma" && (
            <section className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                Idioma
              </h2>
              <p className="mt-1 text-sm text-foreground-600">
                Idioma preferido de la interfaz.
              </p>
              <div className="mt-4 flex items-center gap-2">
                {[
                  { value: "es", label: "Español" },
                  { value: "en", label: "English" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setLanguage(opt.value)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      language === opt.value
                        ? "bg-primary-500 text-white"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </section>
          )}

          {active === "seguridad" && (
            <SecuritySection onResetPassword={handleResetPassword} />
          )}

          {(active === "vendedor" ||
            active === "membresia" ||
            active === "notificaciones") && (
            <StatusSections active={active} profile={profile} />
          )}

          {/* Botón guardar para secciones de texto */}
          {TEXT_SECTIONS.includes(active) && (
            <div className="mt-5 flex items-center justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Guardando…
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    Guardar cambios
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}